import React, { useState } from 'react';
import { ModelArtifact } from 'src/context/apiState';
import { ClientSideTable } from 'src/components/ClientSideTable';
import { Box, SpaceBetween } from '@cloudscape-design/components';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { apiGetRequest } from 'src/utils/mlPigeonAPIRequest';
import { urlList } from 'src/context/url';
import { Link } from './Link';
import { VariableListCell } from './VariableListCell';
import { initialMetricsPublisher } from 'src/utils/metrics';
import { PermissionAwareButton } from 'src/components/PermissionAwareButton';
import { ModelArtifactModal } from 'src/components/ModelArtifactModal';

export function ModelArtifactTable() {
  const { modelVersionId } = useParams();
  const { data, isLoading, error } = useQuery<ModelArtifact[], Error>({
    queryKey: ['modelArtifacts', modelVersionId],
    queryFn: () => {
      return apiGetRequest({
        endpoint: urlList.modelArtifacts,
        params: {
          modelVersionId: modelVersionId || '',
        },
      });
    },
  });

  const actionMetricsPublisher =
    initialMetricsPublisher.newChildActionPublisherForMethod('RedeployPage');

  const [modelArtifactModalConfig, setModelArtifactModalConfig] = useState<{
    modelVersionId: string;
    modelArtifactId: string;
  } | null>(null);

  return (
    <>
      <ClientSideTable
        data={data ?? []}
        title="Model Artifacts"
        description="MLPigeon Model Artifacts are a collection of operator flows. You can view the aggregated metrics for all flows in an artifact, for both model metrics and data quality metrics (if enabled). SageMaker Model Artifacts are not related."
        error={error}
        loading={isLoading}
        columnDefinitions={[
          {
            id: 'model-artifact-name',
            header: 'Name',
            cell: (item) => item.modelArtifactName,
            sortingField: 'modelArtifactName',
            isRowHeader: true,
          },
          {
            id: 'model-artifact-id',
            header: 'Id',
            cell: (item) => item.id,
            width: 25,
          },
          {
            id: 'variables',
            header: 'Variables',
            cell: (item) => <VariableListCell variables={item.tags} />,
          },
          {
            id: 'links',
            header: 'Links',
            cell: (item) => (
              <SpaceBetween size="xs" direction="horizontal">
                <Link to={`./modelArtifacts/${item.id}/operatorFlowJobs`}>
                  Operator Flows
                </Link>
                <Link to={`./modelArtifacts/${item.id}/monitoring`}>
                  Model Metrics
                </Link>
                <Link to={`./modelArtifacts/${item.id}/dataQualityMetrics`}>
                  Data Quality Metrics
                </Link>
                <Link
                  to={`./modelArtifacts/${item.id}/endpoints`}
                  onClick={() => {
                    actionMetricsPublisher?.publishCounterMonitor(
                      'FromModelArtifactTable',
                      1,
                    );
                  }}
                >
                  Endpoint Redeployment
                </Link>
              </SpaceBetween>
            ),
            width: 500,
          },
          {
            id: 'actions',
            header: 'Actions',
            cell: (modelArtifact: ModelArtifact) => (
              <SpaceBetween
                direction="horizontal"
                size="xs"
                alignItems="center"
              >
                <PermissionAwareButton
                  onClick={() =>
                    setModelArtifactModalConfig({
                      modelVersionId: modelArtifact.modelVersionId,
                      modelArtifactId: modelArtifact.id,
                    })
                  }
                  variant="normal"
                  wrapText={false}
                  requiredPermission="ADMINISTER"
                  actionDescription="delete model artifacts"
                >
                  Delete
                </PermissionAwareButton>
              </SpaceBetween>
            ),
          },
        ]}
        loadingText="Loading artifacts"
        empty={
          <Box margin={{ vertical: 'xs' }} textAlign="center" color="inherit">
            <SpaceBetween size="m">
              <b>
                Existing or New Model Artifacts will be listed here when an
                operator flow begins.
              </b>
            </SpaceBetween>
          </Box>
        }
        trackBy="id"
      />
      <ModelArtifactModal
        values={modelArtifactModalConfig}
        visible={!!modelArtifactModalConfig}
        onClose={() => setModelArtifactModalConfig(null)}
      />
    </>
  );
}
